import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  TEMPLATE_KEY_FEATURES, TEMPLATE_STARTS,
  type HandsetModel, type ProvisioningTemplate, type TemplateKeyKind, type TemplateStart,
} from '@arcnet/contracts';
import {
  useCreateTemplate, useDeleteTemplate, useDuplicateTemplate, useSaveTemplate, useTemplates,
  type DuplicatedTemplate,
} from '../../lib/api/hooks-templates.js';
import { useHandsetCatalogue } from '../../lib/api/hooks-provisioning.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { ApiClientError } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { useSubroute } from '../../lib/subroute.js';
import { relativeTime } from '../../lib/format.js';
import { Badge, Empty, ErrorBanner, Segmented, TableSkeleton } from '../../components/ui/Primitives.js';
import { FormActions } from '../../components/ui/Form.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Phone templates (docs/29, owner 15 September 2026): a handset model, its keys and the
 * network its phones share. An extension gets a phone by its MAC and one of these.
 *
 * One screen for both consoles. With an account it lists that account's templates with the
 * platform's beside them, read-only; without one (/inventory/templates) it is the platform's
 * own. The open template is the last part of the address, by its slug.
 *
 * Phase 1 edits keys as a list, one row per key on the phone. The drawn phone is Phase 2 and
 * edits the same draft.
 */

const START_LABEL: Record<TemplateStart, string> = {
  blank: 'Blank', back_office: 'Back office', receptionist: 'Receptionist', agent: 'Agent',
  hot_desking: 'Hot desking',
};

const KIND_LABEL: Record<TemplateKeyKind, string> = {
  line: 'Line', blf: 'Busy lamp', blf_next: 'Next colleague', speed_dial: 'Speed dial',
  feature: 'Feature', desk_login: 'Log in', desk_logout: 'Log out',
};

const FEATURE_LABEL: Record<(typeof TEMPLATE_KEY_FEATURES)[number], string> = {
  voicemail_own: 'Voicemail', dnd_on: 'DND on', dnd_off: 'DND off', fwd_off: 'Forward off',
  queue_pause: 'Pause', queue_unpause: 'Resume',
};

const phones = (n: number): string => `${n} phone${n === 1 ? '' : 's'}`;

export function TemplatesPage({ tenantId }: { tenantId: string | null }) {
  const route = useSubroute();
  const templates = useTemplates(tenantId);
  const slug = route.rest[0];

  const list = templates.data?.data ?? [];
  // An account's own template before a platform one of the same name.
  const open = slug
    ? list.find((t) => t.slug === slug && t.tenant_id !== null) ?? list.find((t) => t.slug === slug) ?? null
    : null;

  if (slug && open) {
    return (
      <TemplateEditor key={`${open.id}:${open.version}`} tenantId={tenantId} template={open}
        onBack={() => route.go([])} onOpen={(s) => route.go([s])} />
    );
  }

  return (
    <TemplateList tenantId={tenantId} templates={list} loading={templates.isLoading}
      error={templates.error} missing={slug && !templates.isLoading ? slug : null}
      onOpen={(s) => route.go([s])} />
  );
}

function TemplateList({ tenantId, templates, loading, error, missing, onOpen }: {
  tenantId: string | null; templates: ProvisioningTemplate[]; loading: boolean; error: unknown;
  missing: string | null; onOpen: (slug: string) => void;
}) {
  const can = useSession((s) => s.can);
  const writable = can('provisioningtemplate:write');
  const [adding, setAdding] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Phone templates</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            A handset model, its keys and its network. Many phones can share one.
          </p>
        </div>
        <div className="arc-spacer" />
        {writable && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setAdding((v) => !v)}>
            <Icon name={adding ? 'close' : 'plus'} size={13} />
            {adding ? 'Cancel' : 'New template'}
          </button>
        )}
      </div>

      {error !== null && error !== undefined && <ErrorBanner error={error} />}
      {missing && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>There is no template called {missing}.</span>
        </div>
      )}

      {adding && <NewTemplate tenantId={tenantId} onCreated={(t) => { setAdding(false); onOpen(t.slug); }} />}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Template</th><th>Handset</th><th className="arc-td-num">Keys</th>
                <th className="arc-td-num">Phones</th><th>Changed</th>
              </tr>
            </thead>
            {loading ? <TableSkeleton cols={5} /> : (
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id} className="arc-clickable" onClick={() => onOpen(t.slug)}>
                    <td>
                      <span style={{ fontWeight: 550 }}>{t.name}</span>
                      {tenantId && t.tenant_id === null && <> <Badge tone="muted">all accounts</Badge></>}
                      {t.extension_number && <> <Badge tone="info">{t.extension_number}’s own</Badge></>}
                    </td>
                    <td className="arc-muted">{t.vendor_label} {t.model}</td>
                    <td className="arc-td-num arc-muted">{t.keys.length}</td>
                    <td className="arc-td-num arc-muted">{t.phone_count || <span className="arc-subtle">—</span>}</td>
                    <td className="arc-muted">{relativeTime(t.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!loading && templates.length === 0 && (
          <Empty icon="phone" title="No templates"
            hint="Start one from a layout: back office, receptionist, agent or hot desking." />
        )}
      </div>
    </>
  );
}

/** Offered models by make, for choosing a template's handset. */
function ModelSelect({ id, value, onChange, models, keep }: {
  id: string; value: string; onChange: (v: string) => void; models: HandsetModel[];
  /** A retired model the template is already on stays choosable. */
  keep?: string;
}) {
  const groups = new Map<string, HandsetModel[]>();
  for (const m of models) {
    if (!m.active && m.id !== keep) continue;
    groups.set(m.vendor_label, [...(groups.get(m.vendor_label) ?? []), m]);
  }
  return (
    <select id={id} className="arc-input" value={value} required onChange={(e) => onChange(e.target.value)}>
      <option value="" disabled>Choose a handset…</option>
      {[...groups].map(([make, ms]) => (
        <optgroup key={make} label={make}>
          {ms.map((m) => <option key={m.id} value={m.id}>{m.vendor_label} {m.model}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function NewTemplate({ tenantId, onCreated }: {
  tenantId: string | null; onCreated: (t: ProvisioningTemplate) => void;
}) {
  const create = useCreateTemplate(tenantId);
  const catalogue = useHandsetCatalogue();
  const [name, setName] = useState('');
  const [modelId, setModelId] = useState('');
  const [start, setStart] = useState<TemplateStart>('back_office');

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        create.mutate({ name: name.trim(), handset_model_id: modelId, start_from: start }, { onSuccess: onCreated });
      }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-name">Name</label>
          <input id="tpl-name" className="arc-input" value={name} maxLength={60} required
            placeholder="Reception" onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-model">Handset</label>
          <ModelSelect id="tpl-model" value={modelId} onChange={setModelId} models={catalogue.data?.data ?? []} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-start">Start from</label>
          <select id="tpl-start" className="arc-input" value={start}
            onChange={(e) => setStart(e.target.value as TemplateStart)}>
            {TEMPLATE_STARTS.map((s) => <option key={s} value={s}>{START_LABEL[s]}</option>)}
          </select>
        </div>
      </div>
      <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
        <div className="arc-spacer" />
        <button type="submit" className="arc-btn arc-btn-primary"
          disabled={create.isPending || !name.trim() || !modelId}>
          {create.isPending ? 'Creating…' : 'Create template'}
        </button>
      </div>
    </form>
  );
}

/** One key as the editor holds it. Kind '' is a key with nothing on it. */
interface KeyDraft {
  kind: TemplateKeyKind | '';
  account: number | null;
  extension_id: string | null;
  value: string;
  label: string;
}

interface Draft {
  name: string;
  notes: string;
  network_mode: 'unmanaged' | 'dhcp';
  vlan: string;
  keys: Record<number, KeyDraft>;
}

const EMPTY_KEY: KeyDraft = { kind: '', account: null, extension_id: null, value: '', label: '' };

const draftOf = (t: ProvisioningTemplate): Draft => ({
  name: t.name,
  notes: t.notes ?? '',
  network_mode: t.network_mode,
  vlan: t.vlan_id ? String(t.vlan_id) : '',
  keys: Object.fromEntries(t.keys.map((k) => [k.position, {
    kind: k.kind, account: k.account, extension_id: k.extension_id, value: k.value ?? '', label: k.label ?? '',
  }])),
});

function TemplateEditor({ tenantId, template, onBack, onOpen }: {
  tenantId: string | null; template: ProvisioningTemplate;
  onBack: () => void; onOpen: (slug: string) => void;
}) {
  const can = useSession((s) => s.can);
  const save = useSaveTemplate(tenantId);
  const del = useDeleteTemplate(tenantId);
  const extensions = useExtensions(tenantId, { limit: 500 });
  const initial = useMemo(() => draftOf(template), [template]);
  const [draft, setDraft] = useState<Draft>(initial);
  const [extra, setExtra] = useState(0);
  const [duplicating, setDuplicating] = useState(false);
  useEffect(() => { setDraft(initial); }, [initial]);

  // An account sees the platform's templates to choose from, not to change.
  const shared = tenantId !== null && template.tenant_id === null;
  const writable = can('provisioningtemplate:write') && !shared;
  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const fieldErrors = save.error instanceof ApiClientError ? save.error.fieldErrors : {};

  const used = Object.keys(draft.keys).map(Number);
  const rows = template.line_keys ?? Math.min(120, Math.max(10, ...used) + extra);
  const setKey = (position: number, next: Partial<KeyDraft>) =>
    setDraft((d) => ({ ...d, keys: { ...d.keys, [position]: { ...(d.keys[position] ?? EMPTY_KEY), ...next } } }));

  const networkChanged = draft.network_mode !== template.network_mode
    || (draft.vlan.trim() ? Number(draft.vlan) : null) !== template.vlan_id;

  const submit = () => {
    if (template.phone_count > 0) {
      const warning = networkChanged ? ' A wrong VLAN takes them offline.' : '';
      if (!confirm(`Update ${phones(template.phone_count)}? They change at their next fetch.${warning}`)) return;
    }
    save.mutate({
      id: template.id,
      version: template.version,
      expected_phone_count: template.phone_count,
      confirm_network: networkChanged,
      name: draft.name.trim(),
      notes: draft.notes.trim() || null,
      network_mode: draft.network_mode,
      vlan_id: draft.vlan.trim() ? Number(draft.vlan) : null,
      keys: Object.entries(draft.keys)
        .filter(([, k]) => k.kind !== '')
        .map(([position, k]) => ({
          position: Number(position),
          kind: k.kind as TemplateKeyKind,
          account: k.kind === 'line' ? (k.account ?? 1) : null,
          extension_id: k.kind === 'blf' ? k.extension_id : null,
          value: k.kind === 'speed_dial' || k.kind === 'feature' ? k.value || null : null,
          label: k.label.trim() || null,
        })),
    });
  };

  const seats = extensions.data?.data ?? [];

  return (
    <>
      <button type="button" className="arc-crumb" onClick={onBack}
        style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
        <Icon name="arrowLeft" size={13} /> Phone templates
      </button>

      <div className="arc-row" style={{ marginBottom: 14, alignItems: 'flex-start' }}>
        <div>
          <h2 className="arc-h2">{template.name}</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            {template.vendor_label} {template.model} · version {template.version} ·{' '}
            {template.phone_count ? `used by ${phones(template.phone_count)}` : 'no phones yet'}
            {template.extension_number ? ` · ${template.extension_number}’s own` : ''}
            {shared ? ' · shared by every account' : ''}
          </p>
        </div>
        <div className="arc-spacer" />
        <div className="arc-row" style={{ gap: 6 }}>
          {can('provisioningtemplate:write') && (
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => setDuplicating((v) => !v)}>
              {duplicating ? 'Cancel' : 'Duplicate'}
            </button>
          )}
          {writable && template.phone_count === 0 && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={del.isPending}
              onClick={() => {
                if (confirm(`Delete ${template.name}?`)) del.mutate(template.id, { onSuccess: onBack });
              }}>
              Delete
            </button>
          )}
        </div>
      </div>

      {duplicating && <DuplicateTemplate tenantId={tenantId} template={template} onOpen={onOpen} />}

      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="tpl-ed-name">Name</label>
            <input id="tpl-ed-name" className="arc-input" value={draft.name} maxLength={60} disabled={!writable}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <span className="arc-label">Network</span>
            <Segmented label="Network" value={draft.network_mode}
              options={[['unmanaged', 'As the phone has it'], ['dhcp', 'DHCP']]}
              onChange={(mode) => { if (writable) setDraft((d) => ({ ...d, network_mode: mode })); }} />
          </div>
          <div className="arc-field" style={{ margin: 0, maxWidth: 160 }}>
            <label className="arc-label" htmlFor="tpl-ed-vlan">Voice VLAN</label>
            <input id="tpl-ed-vlan" className="arc-input arc-mono" value={draft.vlan} inputMode="numeric"
              placeholder="none" disabled={!writable}
              onChange={(e) => setDraft((d) => ({ ...d, vlan: e.target.value.replace(/\D/g, '').slice(0, 4) }))} />
          </div>
        </div>
        <p className="arc-hint" style={{ marginTop: 8 }}>
          A phone with its own static address keeps its own network settings.
        </p>
      </div>

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Key</th><th style={{ width: 170 }}>Does</th><th>Target</th><th>Label</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rows }, (_, i) => i + 1).map((position) => {
                const k = draft.keys[position] ?? EMPTY_KEY;
                const error = Object.entries(fieldErrors)
                  .find(([field]) => field.startsWith(`keys.`) && keyFieldPosition(field, draft) === position)?.[1];
                return (
                  <tr key={position}>
                    <td><span className="arc-key-chip" style={{ width: 24, height: 24, fontSize: 11.5 }}>{position}</span></td>
                    <td>
                      <select className="arc-input" value={k.kind} disabled={!writable}
                        aria-label={`Key ${position} does`}
                        onChange={(e) => setKey(position, { ...EMPTY_KEY, kind: e.target.value as TemplateKeyKind | '' })}>
                        <option value="">Nothing</option>
                        {(Object.keys(KIND_LABEL) as TemplateKeyKind[])
                          // A template for every account cannot name somebody.
                          .filter((kind) => kind !== 'blf' || template.tenant_id !== null)
                          .map((kind) => <option key={kind} value={kind}>{KIND_LABEL[kind]}</option>)}
                      </select>
                    </td>
                    <td>
                      {k.kind === 'line' && (
                        <select className="arc-input" value={k.account ?? 1} disabled={!writable}
                          aria-label={`Key ${position} account`}
                          onChange={(e) => setKey(position, { account: Number(e.target.value) })}>
                          {Array.from({ length: template.max_lines }, (_, a) => a + 1).map((a) => (
                            <option key={a} value={a}>Account {a}</option>
                          ))}
                        </select>
                      )}
                      {k.kind === 'blf' && (
                        <select className="arc-input" value={k.extension_id ?? ''} disabled={!writable}
                          aria-label={`Key ${position} extension`}
                          onChange={(e) => setKey(position, { extension_id: e.target.value || null })}>
                          <option value="" disabled>Choose an extension…</option>
                          {seats.map((s) => <option key={s.id} value={s.id}>{s.number} · {s.display_name}</option>)}
                        </select>
                      )}
                      {k.kind === 'speed_dial' && (
                        <input className="arc-input arc-mono" value={k.value} disabled={!writable}
                          inputMode="tel" placeholder="0115550100" aria-label={`Key ${position} number`}
                          onChange={(e) => setKey(position, { value: e.target.value.replace(/[^0-9*#+]/g, '').slice(0, 32) })} />
                      )}
                      {k.kind === 'feature' && (
                        <select className="arc-input" value={k.value} disabled={!writable}
                          aria-label={`Key ${position} feature`}
                          onChange={(e) => setKey(position, { value: e.target.value })}>
                          <option value="" disabled>Choose a feature…</option>
                          {TEMPLATE_KEY_FEATURES.map((f) => <option key={f} value={f}>{FEATURE_LABEL[f]}</option>)}
                        </select>
                      )}
                      {(k.kind === '' || k.kind === 'blf_next' || k.kind === 'desk_login' || k.kind === 'desk_logout') && (
                        <span className="arc-subtle">—</span>
                      )}
                      {error && <div className="arc-error">{error}</div>}
                    </td>
                    <td>
                      {k.kind !== '' && (
                        <input className="arc-input" value={k.label} maxLength={32} disabled={!writable}
                          placeholder={derivedLabel(k, seats)} aria-label={`Key ${position} label`}
                          onChange={(e) => setKey(position, { label: e.target.value })} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {template.line_keys === null && writable && rows < 120 && (
          <div className="arc-card-body" style={{ paddingTop: 10 }}>
            <button type="button" className="arc-btn arc-btn-sm" onClick={() => setExtra((n) => n + 5)}>
              <Icon name="plus" size={12} /> More keys
            </button>
            <span className="arc-subtle" style={{ fontSize: 11.5, marginLeft: 10 }}>
              The catalogue does not record how many keys a {template.model} has.
            </span>
          </div>
        )}
      </div>

      {writable && (
        <FormActions dirty={dirty} saving={save.isPending} onSave={submit} onReset={() => setDraft(initial)} />
      )}
    </>
  );
}

/** The position a `keys.N.field` error names, where N indexes the keys as they were sent. */
function keyFieldPosition(field: string, draft: Draft): number | null {
  const index = Number(field.split('.')[1]);
  const sent = Object.entries(draft.keys).filter(([, k]) => k.kind !== '').map(([p]) => Number(p));
  return Number.isInteger(index) ? sent[index] ?? null : null;
}

/** What a key shows on the phone when nobody gives it a label. */
function derivedLabel(k: KeyDraft, seats: Array<{ id: string; number: string; display_name: string }>): string {
  switch (k.kind) {
    case 'line': return `Account ${k.account ?? 1}`;
    case 'blf': return seats.find((s) => s.id === k.extension_id)?.display_name ?? 'Busy lamp';
    case 'blf_next': return 'Next colleague';
    case 'speed_dial': return k.value || 'Speed dial';
    case 'feature': return FEATURE_LABEL[k.value as keyof typeof FEATURE_LABEL] ?? 'Feature';
    case 'desk_login': return 'Log in';
    case 'desk_logout': return 'Log out';
    default: return '';
  }
}

function DuplicateTemplate({ tenantId, template, onOpen }: {
  tenantId: string | null; template: ProvisioningTemplate; onOpen: (slug: string) => void;
}) {
  const dup = useDuplicateTemplate(tenantId);
  const catalogue = useHandsetCatalogue();
  const [name, setName] = useState(`${template.name} copy`.slice(0, 60));
  const [modelId, setModelId] = useState(template.handset_model_id);
  const [done, setDone] = useState<DuplicatedTemplate | null>(null);

  if (done) {
    return (
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-row" style={{ gap: 8 }}>
          <span>
            Created {done.template.name}.
            {done.dropped.length > 0 && ` Key${done.dropped.length === 1 ? '' : 's'} ${done.dropped.join(', ')} did not fit a ${done.template.model} and ${done.dropped.length === 1 ? 'was' : 'were'} left out.`}
          </span>
          <div className="arc-spacer" />
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => onOpen(done.template.slug)}>
            Open
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        dup.mutate({ id: template.id, name: name.trim(), handset_model_id: modelId }, { onSuccess: setDone });
      }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-dup-name">Name</label>
          <input id="tpl-dup-name" className="arc-input" value={name} maxLength={60} required
            onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="tpl-dup-model">Handset</label>
          <ModelSelect id="tpl-dup-model" value={modelId} onChange={setModelId}
            models={catalogue.data?.data ?? []} keep={template.handset_model_id} />
        </div>
      </div>
      <div className="arc-row" style={{ gap: 8, marginTop: 12 }}>
        <span className="arc-subtle" style={{ fontSize: 11.5 }}>
          {tenantId && template.tenant_id === null ? 'The copy is this account’s own, and can be changed.' : 'Keys another handset does not have are left out.'}
        </span>
        <div className="arc-spacer" />
        <button type="submit" className="arc-btn arc-btn-primary" disabled={dup.isPending || !name.trim() || !modelId}>
          {dup.isPending ? 'Duplicating…' : 'Duplicate'}
        </button>
      </div>
    </form>
  );
}
